/**
 * Inquiry table component for displaying paginated inquiry list
 * Responsive design: Table on desktop, Cards on mobile
 */

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InquiryStatusBadge } from './InquiryStatusBadge';
import { InquiryRow } from '@/modules/marketplace/types/inquiries.types';

interface InquiryTableProps {
  inquiries: InquiryRow[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  currentPage?: number;
  totalPages?: number;
  onRowClick?: (inquiry: InquiryRow) => void;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  onRetry?: () => void;
}

export function InquiryTable({
  inquiries,
  isLoading = false,
  isError = false,
  error = null,
  currentPage = 0,
  totalPages = 1,
  onRowClick,
  onNextPage,
  onPrevPage,
  onRetry,
}: InquiryTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Helper to format quantity display
  const formatQuantity = (inquiry: InquiryRow) => {
    if (inquiry.quantity && inquiry.quantityUnit) {
      return `${inquiry.quantity} ${inquiry.quantityUnit}`;
    } else if (inquiry.quantity) {
      return `${inquiry.quantity}`;
    }
    return '-';
  };

  // Desktop table view
  const desktopView = (
    <div className="hidden md:block border rounded-lg overflow-hidden">
      <Table>
        <TableHeader className="bg-gray-50">
          <TableRow>
            <TableHead className="font-semibold">Date</TableHead>
            <TableHead className="font-semibold">Product</TableHead>
            <TableHead className="font-semibold">Customer Name</TableHead>
            <TableHead className="font-semibold">Quantity</TableHead>
            <TableHead className="font-semibold">Location</TableHead>
            <TableHead className="font-semibold">Status</TableHead>
            <TableHead className="font-semibold text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inquiries.map((inquiry) => (
            <TableRow
              key={inquiry.id}
              className="hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onRowClick?.(inquiry)}
            >
              <TableCell className="text-sm">{inquiry.date}</TableCell>
              <TableCell className="text-sm font-medium">{inquiry.product}</TableCell>
              <TableCell className="text-sm">{inquiry.customerName}</TableCell>
              <TableCell className="text-sm">{formatQuantity(inquiry)}</TableCell>
              <TableCell className="text-sm">{inquiry.location || '-'}</TableCell>
              <TableCell>
                <InquiryStatusBadge status={inquiry.status} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowClick?.(inquiry);
                  }}
                  className="gap-1"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  // Mobile card view
  const mobileView = (
    <div className="md:hidden space-y-3">
      {inquiries.map((inquiry) => (
        <Card
          key={inquiry.id}
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onRowClick?.(inquiry)}
        >
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <CardTitle className="text-base">{inquiry.customerName}</CardTitle>
                <p className="text-sm text-gray-500">{inquiry.product}</p>
              </div>
              <InquiryStatusBadge status={inquiry.status} size="sm" />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Quantity and Location for B2B */}
            {(inquiry.quantity || inquiry.location) && (
              <div className="flex flex-wrap gap-2 text-xs">
                {inquiry.quantity && (
                  <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">
                    Qty: {formatQuantity(inquiry)}
                  </span>
                )}
                {inquiry.location && (
                  <span className="bg-green-50 text-green-700 px-2 py-1 rounded">
                    {inquiry.location}
                  </span>
                )}
              </div>
            )}
            <p className="text-sm text-gray-600 line-clamp-2">{inquiry.message}</p>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">{inquiry.date}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick?.(inquiry);
                }}
                className="gap-1"
              >
                <Eye className="h-4 w-4" />
                View
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="hidden md:block space-y-2 border rounded-lg p-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
        <div className="md:hidden space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>{error?.message || 'Failed to load inquiries'}</span>
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="ml-2"
            >
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Empty state
  if (inquiries.length === 0) {
    return (
      <Card>
        <CardContent className="pt-8 text-center">
          <p className="text-gray-500 mb-4">No inquiries found</p>
          <p className="text-sm text-gray-400">Try adjusting your filters</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Table/Cards */}
      {desktopView}
      {mobileView}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Page {currentPage + 1} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrevPage}
              disabled={currentPage === 0}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onNextPage}
              disabled={currentPage === totalPages - 1}
              className="gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
